<template>
  <CCard class="dataTable py-5 mt-3">
    <transition name="fade">
      <MenssageAlert :messages="messages" />
    </transition>
    <CRow class="pt-5 pb-4">
      <CCol class="d-flex flex-column">
        <div>
          <h2 class="titlePage font-weight-normal text-capitalize">
            {{ $t('whitelabel.config.header') }}
          </h2>
        </div>
      </CCol>
    </CRow>
    <CSpinner v-if="isLoadingWhitelabelConfig || isLoadingWhitelabelDelete" color="primary" />
    <template v-else>
      <div class="mb-4">
        <span class="align-middle mr-2 font-xl text-gray-700">{{ $t('whitelabel.config.toggle') }}</span>
        <div>
          <CSwitch
            class="align-middle switch-custom"
            color="primary"
            shape="pill"
            size="lg"
            :checked.sync="whitelabelToggle"
            :disabled="showConfirmationPopup"
            @update:checked="(val) => toggleWhitelabel(val)"
          />
          <ConfirmationPopup
            class="ml-5 mb-0"
            v-show="showConfirmationPopup"
            :popupDescriptionT="$t('whitelabel.config.toggle-disabled')"
            :hasConfirmationText="false"
            @confirm="deleteWhitelabelConfig"
            @cancel="handleCancelPopup"
          />
        </div>
      </div>
      <div v-if="whitelabelToggle">
        <hr />
        <div class="mb-4">
          <label class="font-xl text-gray-700 d-block" for="logoFileInput"
            >{{ $t('whitelabel.config.logo.label') }} <span class="font-sm">({{ acceptedImageFormatsLabel }})</span>
          </label>
          <div class="d-flex">
            <div class="inputUpload input-width" :class="{ 'inputUpload--disabled': false }">
              <CIcon name="upload" :height="18" class="px-3 text-gray-700" viewBox="0 0 32 32" />
              <CInputFile
                name="logoFileInput"
                :label="$t('whitelabel.config.file-upload')"
                :accept="acceptedImageFormats"
                :value.sync="logoImageSync"
                @input="(event) => onFileInput(event, 'logoImage')"
              />
            </div>
            <div class="position-relative" v-if="logoImageUrl">
              <img :src="logoImageUrl" style="width: 100px" />
              <CButton class="clearBtnImage" @click="clearFileUpload('logoImage')" style="width: 50px">
                <CIcon name="trash" :height="18" class="text-gray-700" viewBox="0 0 32 32" />
              </CButton>
            </div>
          </div>
        </div>
        <div class="mb-4">
          <label class="font-xl text-gray-700 d-block" for="loginScreenFileInput"
            >{{ $t('whitelabel.config.login-screen.label') }} <span class="font-sm">({{ acceptedImageFormatsLabel }})</span>
          </label>
          <div class="d-flex">
            <div class="inputUpload input-width" :class="{ 'inputUpload--disabled': false }">
              <CIcon name="upload" :height="18" class="px-3 text-gray-700" viewBox="0 0 32 32" />
              <CInputFile
                name="loginScreenFileInput"
                :label="$t('whitelabel.config.file-upload')"
                :accept="acceptedImageFormats"
                :value.sync="loginScreenImageSync"
                @input="(event) => onFileInput(event, 'loginScreenImage')"
              />
            </div>
            <div class="position-relative" v-if="loginScreenImageUrl">
              <img :src="loginScreenImageUrl" style="width: 100px" />
              <CButton class="clearBtnImage" @click="clearFileUpload('loginScreenImage')" style="width: 50px">
                <CIcon name="trash" :height="18" class="text-gray-700" viewBox="0 0 32 32" />
              </CButton>
            </div>
          </div>
        </div>
        <div class="mb-4">
          <label class="font-xl text-gray-700 d-block" for="domainUrlInput">
            {{ $t('whitelabel.config.domain-url.label') }} <span class="text-danger">*</span> <span class="font-sm">(eg: arpoone.com)</span>
          </label>
          <CInput
            class="input-width"
            name="domainUrlInput"
            type="text"
            :value.sync="$v.form.domain.$model"
            :invalidFeedback="$t('whitelabel.config.domain-url.invalid')"
            :isValid="checkIfValid('domain')"
          />
        </div>
        <div class="mb-4">
          <label class="font-xl text-gray-700 d-block" for="brandNameInput">
            {{ $t('whitelabel.config.brand-name.label') }} <span class="text-danger">*</span>
          </label>
          <CInput
            class="input-width"
            name="brandNameInput"
            type="text"
            :value.sync="$v.form.brandName.$model"
            :invalidFeedback="invalidFeedbackBrandName"
            :isValid="checkIfValid('brandName')"
          />
        </div>
        <div class="mb-4">
          <label class="font-xl text-gray-700 d-block" for="contactEmailAdressInput">
            {{ $t('whitelabel.config.contact-email-address.label') }} <span class="text-danger">*</span>
          </label>
          <CInput
            class="input-width"
            name="contactEmailAdressInput"
            type="text"
            :value.sync="$v.form.contactEmailAddress.$model"
            :invalidFeedback="invalidFeedbackContactEmailAddress"
            :isValid="checkIfValid('contactEmailAddress')"
          />
        </div>
        <CButton :disabled="isBtnMainDisabled" @click.stop="handleBtnMainClick" class="px-4 py-2 btn btn-primary">
          <span class="align-middle">{{ btnMainText }}</span>
          <CSpinner v-if="isLoadingWhitelabelMainAction" class="ml-2 align-middle" style="width: 1rem; height: 1rem" />
        </CButton>
      </div>
      <ImageCropperModal
        v-if="cropperImage"
        :show-modal="showCropperModal"
        :imageSrc="cropperImage"
        :imageName="cropperImageName"
        @showUpdated="handleCropperModalShowUpdated"
        @imageCropped="handleCroppedResult"
      />
    </template>
  </CCard>
</template>

<script>
import { getErrorMessage } from '@/utils/errorHandling.js'
import services from '@/services.js'
import MenssageAlert from '@/components/MenssageAlert'
import ConfirmationPopup from '@/components/ConfirmationPopup'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import { email } from 'vuelidate/lib/validators'
import { maxLength } from 'vuelidate/lib/validators'
import ImageCropperModal from '../../../components/ImageCropperModal.vue'

const LIMIT_SIZE_LOGO = 500
const LIMIT_SIZE_IMG = 1000

const imgLimitEnum = {
  logoImage: LIMIT_SIZE_LOGO,
  loginScreenImage: LIMIT_SIZE_IMG,
}

const BRAND_NAME_MAX_LENGTH = 50

const FILE_TYPES = ['image/png', 'image/jpeg', 'image/svg']

export default {
  name: 'WhitelabelConfig',
  components: { MenssageAlert, ConfirmationPopup, ImageCropperModal },
  data() {
    return {
      isLoadingWhitelabelConfig: false,
      isLoadingWhitelabelDelete: false,
      isLoadingWhitelabelMainAction: false,
      messages: [],
      whitelabelConfig: undefined,
      whitelabelToggle: false,
      acceptedImageFormats: FILE_TYPES.toString(),
      showConfirmationPopup: false,
      form: {
        logoImage: undefined,
        loginScreenImage: undefined,
        domain: undefined,
        brandName: undefined,
        contactEmailAddress: undefined,
      },
      logoImageUrl: undefined,
      loginScreenImageUrl: undefined,
      logoImageSync: undefined,
      loginScreenImageSync: undefined,
      showCropperModal: false,
      cropperImage: undefined,
      cropperImageName: undefined,
      cropperField: undefined,
    }
  },
  mixins: [validationMixin],
  validations: {
    form: {
      domain: {
        required,
        validUrl(domain) {
          const urlRegex = /^(http:\/\/|https:\/\/)?(www\.)?([a-zA-Z0-9-]+\.){1,}[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?$/

          return urlRegex.test(domain)
        },
      },
      brandName: {
        required,
        maxLength: maxLength(BRAND_NAME_MAX_LENGTH),
      },
      contactEmailAddress: {
        required,
        email,
      },
    },
  },
  mounted() {
    this.getWhitelabelConfig()
  },
  computed: {
    acceptedImageFormatsLabel() {
      const typesArray = this.acceptedImageFormats.split(',')
      const formattedTypes = typesArray.map((type) => {
        const format = type.split('/')[1]
        return format.trim()
      })
      return formattedTypes.join(', ')
    },
    btnMainText() {
      return this.whitelabelConfig ? this.$t('whitelabel.config.btn.save') : this.$t('whitelabel.config.btn.create')
    },
    isBtnMainDisabled() {
      return this.$v.form.$invalid
    },
    invalidFeedbackBrandName() {
      if (!this.$v.form.brandName.required) return this.$t('whitelabel.config.brand-name.required')
      if (!this.$v.form.brandName.maxLength) return this.$t('whitelabel.config.brand-name.maxLength', [BRAND_NAME_MAX_LENGTH])
    },
    invalidFeedbackContactEmailAddress() {
      if (!this.$v.form.contactEmailAddress.required) return this.$t('whitelabel.config.contact-email-address.required')
      if (!this.$v.form.contactEmailAddress.maxLength) return this.$t('whitelabel.config.contact-email-address.email-invalid')
    },
  },
  methods: {
    checkIfValid(fieldName) {
      const field = this.$v.form[fieldName]
      if (!field.$dirty) {
        return null
      }

      return !(field.$invalid || null)
    },
    toggleWhitelabel(val) {
      this.whitelabelToggle = val

      if (this.whitelabelConfig) {
        this.showConfirmationPopup = !val
      }
    },
    async handleImageLoad(field, imageUrl) {
      this[`${field}Url`] = imageUrl
      await fetch(imageUrl)
        .then((response) => {
          return response.blob()
        })
        .then((blob) => {
          let splited = imageUrl.split('/').pop()
          this.form[field] = new File([blob], splited, { type: blob.type })
        })
    },
    async getWhitelabelConfig() {
      try {
        this.isLoadingWhitelabelConfig = true
        const response = await services.getEditConfiguration(this.$route.params.organizationId)
        this.whitelabelConfig = response.data.data

        if (this.whitelabelConfig) {
          this.whitelabelToggle = true
          this.form.domain = this.whitelabelConfig.domain
          this.form.brandName = this.whitelabelConfig.brandName
          this.form.contactEmailAddress = this.whitelabelConfig.contactEmailAddress
          if (this.whitelabelConfig.logoUrl) {
            this.handleImageLoad('logoImage', this.whitelabelConfig.logoUrl)
          }
          if (this.whitelabelConfig.loginScreenImageUrl) {
            this.handleImageLoad('loginScreenImage', this.whitelabelConfig.loginScreenImageUrl)
          }
        }
      } catch (error) {
        this.messages = [getErrorMessage(error?.response?.data)]
      } finally {
        this.isLoadingWhitelabelConfig = false
      }
    },
    isImageFileValid(file, field) {
      this.messages = []
      if (file[0].size > imgLimitEnum[field] * 1024 /* to KB */) {
        this.messages = [
          {
            status: 'danger',
            text: this.$t('whitelabel.config.image.limit.size-exceeded', [imgLimitEnum[field]]),
          },
        ]
        return false
      }

      if (!FILE_TYPES.includes(file[0].type)) {
        this.messages = [
          {
            status: 'danger',
            text: this.$t('whitelabel.config.image.type.invalid', [this.acceptedImageFormatsLabel]),
          },
        ]

        return false
      }

      return true
    },
    onFileInput(event, field) {
      const file = event.target.files

      this.clearFileUpload(field)
      if (!file[0]) return

      if (!this.isImageFileValid(file, field)) return

      this.cropperImageName = file[0].name
      this.cropperField = field

      const reader = new FileReader()
      reader.onload = (e) => {
        this.cropperImage = e.target.result

        this.showCropperModal = true
      }
      reader.readAsDataURL(file[0])
    },
    clearFileUpload(field) {
      this.form[field] = null
      this[`${field}Url`] = null
      this.cropperImageName = null
      this.cropperField = null
      this.cropperImage = null
    },
    clearData() {
      this.whitelabelConfig = null
      this.form.domain = null
      this.form.brandName = null
      this.form.contactEmailAddress = null
      this.clearFileUpload('loginScreenImage')
      this.clearFileUpload('logoImage')
      this.$v.form.$reset()
    },
    handleCancelPopup() {
      this.showConfirmationPopup = false
      this.whitelabelToggle = true
    },
    reloadPage() {
      setTimeout(() => {
        window.location.reload()
      }, 2000)
    },
    async deleteWhitelabelConfig() {
      try {
        this.isLoadingWhitelabelDelete = true
        this.showConfirmationPopup = false
        await services.deleteWhitelabelConfig(this.$route.params.organizationId)
        this.clearData()
        this.reloadPage()
      } catch (error) {
        this.whitelabelToggle = true
        this.messages = [getErrorMessage(error?.response?.data)]
      } finally {
        this.isLoadingWhitelabelDelete = false
      }
    },
    async createWhitelabelConfig() {
      try {
        this.isLoadingWhitelabelMainAction = true
        const form = new FormData()
        form.append('domain', this.form.domain)
        form.append('logoImage', this.form.logoImage)
        form.append('loginScreenImage', this.form.loginScreenImage)
        form.append('brandName', this.form.brandName)
        form.append('contactEmailAddress', this.form.contactEmailAddress)

        const response = await services.createWhitelabelConfig(this.$route.params.organizationId, form)
        this.whitelabelConfig = response.data.data
        this.messages = [
          {
            status: 'success',
            text: this.$t('whitelabel.config.create.success'),
          },
        ]
        this.reloadPage()
      } catch (error) {
        this.messages = [getErrorMessage(error?.response?.data)]
      } finally {
        this.isLoadingWhitelabelMainAction = false
      }
    },
    async saveWhitelabelConfig() {
      try {
        this.isLoadingWhitelabelMainAction = true
        const form = new FormData()
        form.append('domain', this.form.domain)
        form.append('logoImage', this.form.logoImage)
        form.append('loginScreenImage', this.form.loginScreenImage)
        form.append('brandName', this.form.brandName)
        form.append('contactEmailAddress', this.form.contactEmailAddress)

        const response = await services.updateWhitelabelConfig(this.$route.params.organizationId, form)
        this.whitelabelConfig = response.data.data
        this.messages = [
          {
            status: 'success',
            text: this.$t('whitelabel.config.update.success'),
          },
        ]
        this.reloadPage()
      } catch (error) {
        this.messages = [getErrorMessage(error?.response?.data)]
      } finally {
        this.isLoadingWhitelabelMainAction = false
      }
    },
    handleBtnMainClick() {
      if (this.whitelabelConfig) {
        this.saveWhitelabelConfig()
        return
      }
      this.createWhitelabelConfig()
    },
    handleCropperModalShowUpdated(value) {
      this.showCropperModal = value
    },
    handleCroppedResult(canvas) {
      this.showCropperModal = false
      canvas.toBlob((blob) => {
        this[`${this.cropperField}Url`] = URL.createObjectURL(blob)

        this.form[this.cropperField] = new File([blob], this.cropperImageName, { type: blob.type })
      })
    },
  },
}
</script>

<style lang="scss">
.switch-custom span {
  background-color: $gray-500;
}
</style>

<style lang="scss" scoped>
.input-width {
  width: 200px;
}
.clearBtnImage {
  position: absolute;
  top: 0;
  left: 60px;
}
</style>
